import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Box, TextField, Button,
  Stack, List, ListItem, ListItemButton, ListItemText, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { api, getContentText } from '../api';
import { useAuth } from '../context/AuthContext';
import { MentionCard } from '../components/MentionCard';

const AlertsPage = ({ state, refreshData, onGenerateReply }) => {
    const [newAlertKeyword, setNewAlertKeyword] = useState('');
    const [newAlertEmail, setNewAlertEmail] = useState('');
    const [isScanning, setIsScanning] = useState(false);
    const [selectedAlertId, setSelectedAlertId] = useState(null);
    const { user } = useAuth();

    // State for the "Edit Alert" dialog
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingAlert, setEditingAlert] = useState(null);
    const [editForm, setEditForm] = useState({ keyword: '', email: '' });
    
    useEffect(() => {
        if (user && !newAlertEmail) setNewAlertEmail(user.email);
    }, [user, newAlertEmail]);

    // Calculate alert trigger counts
    const alertCounts = useMemo(() => {
        const counts = new Map();
        for (const triggered of (state.triggeredAlerts || [])) {
            counts.set(triggered.alertId, (counts.get(triggered.alertId) || 0) + 1);
        }
        return counts;
    }, [state.triggeredAlerts]);

    const handleAddAlert = async (e) => {
        e.preventDefault();
        if (!newAlertKeyword || !newAlertEmail) return alert('Keyword and email are required.');
        try {
            await api.post('/alerts', { keyword: newAlertKeyword, email: newAlertEmail });
            setNewAlertKeyword('');
            refreshData();
        } catch (err) { alert('Failed to add alert rule.'); }
    };

    const handleDeleteAlert = async (id) => {
        if (window.confirm('Are you sure you want to delete this alert rule?')) {
            try {
                await api.delete(`/alerts/${id}`);
                if (selectedAlertId === id) setSelectedAlertId(null);
                refreshData();
            } catch (err) { alert('Failed to delete alert rule.'); }
        }
    };

    // Handlers for the Edit Dialog
    const handleOpenEdit = (alert) => {
        setEditingAlert(alert);
        setEditForm({ keyword: alert.keyword, email: alert.email });
        setEditDialogOpen(true);
    };

    const handleCloseEdit = () => {
        setEditDialogOpen(false);
        setEditingAlert(null);
        setEditForm({ keyword: '', email: '' });
    };

    const handleSaveEdit = async () => {
        if (!editingAlert) return;
        try {
            await api.put(`/alerts/${editingAlert.id}`, editForm);
            handleCloseEdit();
            await refreshData();
        } catch (err) {
            alert('Failed to update alert rule.');
        }
    };

    const handleHistoricalScan = async () => {
        setIsScanning(true);
        try {
            const response = await api.post('/alerts/run-historical');
            alert(response.data.message);
            await refreshData();
        } catch (err) {
            alert('Failed to run historical scan.');
        } finally {
            setIsScanning(false);
        }
    };

    const filteredLogs = selectedAlertId 
        ? (state.triggeredAlerts || []).filter(log => log.alertId === selectedAlertId)
        : (state.triggeredAlerts || []);
    
    // --- THIS FUNCTION IS NO LONGER NEEDED WITH THE NEW FIX ---
    // const formatMention = (rawLog) => ({ ... });

    return (
        <Container maxWidth="xl">
            <Typography variant="h4" gutterBottom>Alerts</Typography>
            <Grid container spacing={3}>
                {/* --- Column 1: Add & Manage Rules --- */}
                <Grid item xs={12} md={5}>
                    <Stack spacing={3}>
                       <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Active Alert Rules</Typography>
                                <List>
                                    <ListItemButton onClick={() => setSelectedAlertId(null)} selected={selectedAlertId === null}>
                                        <ListItemText primary="Show All Triggered Alerts" />
                                    </ListItemButton>
                                    {(state.alerts || []).map(alert => (
                                        <ListItem key={alert.id} divider secondaryAction={
                                            <Stack direction="row" spacing={0.5}>
                                                <IconButton onClick={() => handleOpenEdit(alert)}><EditIcon /></IconButton>
                                                <IconButton onClick={() => handleDeleteAlert(alert.id)}><DeleteIcon /></IconButton>
                                            </Stack>
                                        }>
                                            <ListItemButton onClick={() => setSelectedAlertId(alert.id)} selected={selectedAlertId === alert.id}>
                                                <ListItemText
                                                    primary={
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Typography variant="body1">"{alert.keyword}"</Typography>
                                                            <Chip label={`${alertCounts.get(alert.id) || 0} triggered`} size="small" />
                                                        </Stack>
                                                    }
                                                    secondary={alert.email}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Add Alert Rule</Typography>
                                <Box component="form" onSubmit={handleAddAlert}>
                                    <TextField fullWidth label="Keyword to Monitor" value={newAlertKeyword} onChange={e => setNewAlertKeyword(e.target.value)} sx={{ mb: 2 }} />
                                    <TextField
                                        fullWidth
                                        label="Emails (semicolon-separated)"
                                        type="email"
                                        value={newAlertEmail}
                                        onChange={e => setNewAlertEmail(e.target.value)}
                                        sx={{ mb: 2 }}
                                    />
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        sx={{
                                            backgroundColor: 'black',
                                            '&:hover': { backgroundColor: '#333' }
                                        }}
                                    >
                                        Add Rule
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                        
                        
                        
                        
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Historical Scan</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>Scan all existing data for matches against your current alert rules.</Typography>
                                <Button fullWidth variant="outlined" onClick={handleHistoricalScan} disabled={isScanning}>
                                   {isScanning ? 'Scanning...' : 'Run Scan on All Data'}
                                </Button>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>
                
                {/* --- Column 2: Triggered Alert Log (Layout Fixed) --- */}
               
                     <Box sx={{ maxWidth:'40%', overflowX: 'auto',flex: '1 0 50%' }}>
                          <Card sx={{ height: 1050, display: 'flex',overflowX: 'auto', flexDirection: 'column' }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                              <Typography variant="h6" gutterBottom>Triggerd Alerts</Typography>
                              <Box sx={{ overflow: 'auto', flex: 1 }}>
                                {(filteredLogs || []).slice(0, 50).map(m => <MentionCard key={m.id || m.postUrl} mention={m} />)}
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>
               
            </Grid>

            {/* --- Edit Alert Dialog --- */}
            <Dialog open={editDialogOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
                <DialogTitle>Edit Alert Rule</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <TextField
                            autoFocus
                            label="Keyword"
                            value={editForm.keyword}
                            disabled // Keyword is not editable
                            fullWidth
                        />
                        <TextField
                            label="Emails (semicolon-separated)"
                            value={editForm.email}
                            onChange={e => setEditForm({...editForm, email: e.target.value})}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEdit}>Cancel</Button>
                    <Button onClick={handleSaveEdit} variant="contained">Save Changes</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AlertsPage;

